import { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ServiceHero from './components/ServiceHero';
import ServiceContent from './components/ServiceContent';
import ServiceContactSidebar from './components/ServiceContactSidebar';
import { getServiceBySlug } from '../../mocks/servicePages';

export default function ServicePage() {
  const { pathname } = useLocation();
  const serviceSlug = pathname.replace(/^\//, '').replace(/\/$/, '');
  const service = getServiceBySlug(serviceSlug);

  useEffect(() => {
    if (!service) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const pageUrl = `${siteUrl}/${service.slug}`;

    // Update document title
    document.title = service.metaTitle;

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      (metaDesc as HTMLMetaElement).name = 'description';
      document.head.appendChild(metaDesc);
    }
    (metaDesc as HTMLMetaElement).content = service.metaDescription;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = pageUrl;

    // Schema
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${pageUrl}/#service`,
          'name': service.title,
          'url': pageUrl,
          'description': service.metaDescription,
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'Leak Detectives',
            'telephone': '(725) 267-5482',
            'address': {
              '@type': 'PostalAddress',
              'addressLocality': 'Las Vegas',
              'addressRegion': 'NV',
              'addressCountry': 'US',
            },
          },
          'areaServed': {
            '@type': 'City',
            'name': 'Las Vegas',
          },
        },
        {
          '@type': 'FAQPage',
          'mainEntity': service.faqs.map((faq) => ({
            '@type': 'Question',
            'name': faq.q,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': faq.a,
            },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': siteUrl },
            { '@type': 'ListItem', 'position': 2, 'name': service.title.split(' in Las Vegas')[0], 'item': pageUrl },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = `schema-service-${service.slug}`;
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById(`schema-service-${service.slug}`);
      if (existing) document.head.removeChild(existing);
    };
  }, [service]);

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Navbar />
      <ServiceHero service={service} />

      {/* Main Content + Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Content - 2/3 width */}
          <div className="lg:col-span-2">
            <ServiceContent service={service} />
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="lg:col-span-1">
            <ServiceContactSidebar serviceName={service.title.split(' in Las Vegas')[0]} />
          </div>
        </div>
      </div>

      {/* Final CTA Bar */}
      <section className="bg-gradient-to-r from-[#0a1628] via-red-900 to-[#0a1628] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-red-600 text-white text-xs font-black px-3 py-1.5 rounded-full mb-4">
            DON'T WAIT — THIS PROBLEM GETS WORSE OVER TIME
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4">
            Stop the Damage Before It Gets Worse
          </h2>
          <p className="text-gray-300 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Every hour a leak or plumbing problem goes unaddressed, it costs you more. Call now for same-day service throughout Las Vegas and Clark County.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:7252675482"
              className="flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-5 rounded-lg font-black text-lg hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call Now — (725) 267-5482
            </a>
            <a
              href="#estimate"
              className="flex items-center justify-center gap-2 bg-white text-[#0f1f3d] px-8 py-5 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Estimate
            </a>
          </div>
          <p className="mt-5 text-gray-400 text-sm">Licensed &amp; Insured • Flat-Rate Pricing • No Obligation</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
